import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
    Search, 
    Filter, 
    ChevronUpIcon,
    ChevronDownIcon,
    ChevronLeft, 
    ChevronRight,
    Edit, 
    Trash2, 
    Eye, 
    RefreshCw,
    User,
    Mail,
    Phone,
    Building,
    MapPin,
    CheckCircle,
    XCircle,
    AlertCircle,
    Users,
    Shield,
    UserCheck,
    UserX,
    Calendar,
    TrendingUp,
    Award,
    Briefcase
} from 'lucide-react';

function ViewUsers() {
    // State for users data
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // Pagination state
    const [meta, setMeta] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0
    });
    
    // Filter and search state
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [sortField, setSortField] = useState('created_at');
    const [sortDirection, setSortDirection] = useState('desc');
    const [perPage, setPerPage] = useState(10);

    // Create axios instance
    const api = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        timeout: 30000,
        headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        withCredentials: false
    });

    // Request interceptor to attach token
    api.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem('token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Fetch users
    const fetchUsers = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await api.get('/viewusers');
            const usersData = response.data.users || response.data.user || [];

            setUsers(usersData);
console.log(response)
            setMeta({
                current_page: 1,
                last_page: Math.ceil(usersData.length / perPage),
                per_page: perPage,
                total: usersData.length
            });
        } catch (err) {
            console.error('Error fetching users:', err);
            setError(err.response?.data?.message || err.message || 'Failed to load users');
            Swal.fire({
                title: 'Error!',
                text: err.response?.data?.message || 'Failed to load users',
                icon: 'error',
                confirmButtonColor: '#4F46E5'
            });
        } finally {
            setLoading(false);
        }
    }, [perPage]);

    // Initial fetch
    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    // Calculate statistics
    const stats = useMemo(() => {
        const total = users.length;
        const admins = users.filter(u => u.role === 'admin' || u.role === 'Administrator').length;
        const inspectors = users.filter(u => u.role === 'inspector' || u.role === 'Inspector').length;
        const otherUsers = users.filter(u => u.role !== 'admin' && u.role !== 'inspector' && u.role !== 'Administrator' && u.role !== 'Inspector').length;
        const activeUsers = users.filter(u => (u.status || 'active') === 'active').length;
        const inactiveUsers = users.filter(u => (u.status || 'active') === 'inactive').length;
        
        // Get current month users
        const currentMonth = new Date().getMonth();
        const currentYear = new Date().getFullYear();
        const newThisMonth = users.filter(u => {
            const createdDate = new Date(u.created_at);
            return createdDate.getMonth() === currentMonth && 
                   createdDate.getFullYear() === currentYear;
        }).length;
        
        // Get today's users
        const today = new Date().toISOString().split('T')[0];
        const todayUsers = users.filter(u => {
            const createdDate = new Date(u.created_at).toISOString().split('T')[0];
            return createdDate === today;
        }).length;
        
        // Get recent users (last 7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recentUsers = users.filter(u => new Date(u.created_at) >= sevenDaysAgo).length;
        
        return { 
            total,
            admins,
            inspectors,
            otherUsers,
            activeUsers,
            inactiveUsers,
            newThisMonth,
            todayUsers,
            recentUsers
        };
    }, [users]);

    // Filter and sort users
    const filteredUsers = useMemo(() => {
        let filtered = [...users];
        
        if (searchTerm) {
            filtered = filtered.filter(user => 
                user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                user.role?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        if (roleFilter) {
            filtered = filtered.filter(user => 
                (user.role || '').toLowerCase() === roleFilter.toLowerCase()
            );
        }
        
        if (statusFilter) {
            filtered = filtered.filter(user => 
                (user.status || 'active') === statusFilter
            );
        }
        
        // Apply sorting
        filtered.sort((a, b) => {
            let aVal = a[sortField];
            let bVal = b[sortField];
            
            if (sortField === 'created_at') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            }
            
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        
        return filtered;
    }, [users, searchTerm, roleFilter, statusFilter, sortField, sortDirection]);

    // Update meta when filtered users change
    useEffect(() => {
        setMeta(prev => ({
            ...prev,
            last_page: Math.ceil(filteredUsers.length / perPage),
            total: filteredUsers.length
        }));
    }, [filteredUsers, perPage]);

    // Paginated users
    const paginatedUsers = filteredUsers.slice(
        (meta.current_page - 1) * perPage,
        meta.current_page * perPage
    );

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= meta.last_page) {
            setMeta(prev => ({ ...prev, current_page: newPage }));
        }
    };

    // Handle search change
    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        setMeta(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle role filter
    const handleRoleChange = (e) => {
        setRoleFilter(e.target.value);
        setMeta(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle status filter
    const handleStatusChange = (e) => {
        setStatusFilter(e.target.value);
        setMeta(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle sort
    const handleSort = (field) => {
        if (field === sortField) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
        setMeta(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle per page change
    const handlePerPageChange = (e) => {
        setPerPage(Number(e.target.value));
        setMeta(prev => ({ ...prev, current_page: 1 }));
    };

    // Handle delete
    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: `Delete user "${name}"? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
            background: '#fff',
            customClass: {
                popup: 'rounded-xl',
                confirmButton: 'bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg',
                cancelButton: 'bg-gray-300 hover:bg-gray-400 px-4 py-2 rounded-lg'
            }
        });

        if (result.isConfirmed) {
            try {
                const response = await api.delete(`/users/${id}`);
                
                if (response.data.success) {
                    Swal.fire({
                        title: 'Deleted!',
                        text: 'User deleted successfully',
                        icon: 'success',
                        timer: 2000,
                        timerProgressBar: true,
                        confirmButtonColor: '#4F46E5'
                    });
                    fetchUsers();
                } else {
                    throw new Error(response.data.message || 'Delete failed');
                }
            } catch (err) {
                Swal.fire({
                    title: 'Error!',
                    text: err.response?.data?.message || 'Failed to delete user',
                    icon: 'error',
                    confirmButtonColor: '#EF4444'
                });
            }
        }
    };

    // Handle toggle status
    const handleToggleStatus = async (id, currentStatus) => {
        try {
            const response = await api.patch(`/users/${id}/toggle-status`);
            
            if (response.data.success) {
                Swal.fire({
                    title: 'Success!',
                    text: `User status changed to ${response.data.status}`,
                    icon: 'success',
                    timer: 2000,
                    timerProgressBar: true,
                    confirmButtonColor: '#4F46E5'
                });
                fetchUsers();
            }
        } catch (err) {
            Swal.fire({
                title: 'Error!',
                text: err.response?.data?.message || 'Failed to update status',
                icon: 'error',
                confirmButtonColor: '#EF4444'
            });
        }
    };

    // Handle view
    const handleView = (user) => {
        Swal.fire({
            title: 'User Details',
            html: `
                <div class="text-left">
                    <p><strong>Name:</strong> ${user.name}</p>
                    <p><strong>Email:</strong> ${user.email}</p>
                    <p><strong>Phone:</strong> ${user.phone || '—'}</p>
                    <p><strong>Role:</strong> ${user.role || 'User'}</p>
                    <p><strong>Address:</strong> ${user.address || '—'}</p>
                    <p><strong>Status:</strong> ${user.status || 'active'}</p>
                    <p><strong>Joined:</strong> ${new Date(user.created_at).toLocaleDateString()}</p>
                </div>
            `,
            icon: 'info',
            confirmButtonColor: '#4F46E5',
            customClass: {
                popup: 'rounded-xl'
            }
        });
    };

    // Handle edit
    const handleEdit = (id) => {
        window.location.href = `/admin/edituser/${id}`;
    };

    // Status badge component
    const StatusBadge = ({ status }) => {
        const isActive = status === 'active';
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isActive 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
            }`}>
                {isActive ? <CheckCircle className="w-3 h-3 mr-1" /> : <XCircle className="w-3 h-3 mr-1" />}
                {status || 'active'}
            </span>
        );
    };

    // Role badge component
    const RoleBadge = ({ role }) => {
        const getRoleColor = () => {
            switch(role?.toLowerCase()) {
                case 'admin':
                    return 'bg-purple-100 text-purple-800';
                case 'inspector':
                    return 'bg-blue-100 text-blue-800';
                default:
                    return 'bg-gray-100 text-gray-800';
            }
        };
        
        return (
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor()}`}>
                {role || 'User'}
            </span>
        );
    };

    const SortIcon = ({ field }) => {
        if (sortField !== field) return null;
        return sortDirection === 'asc' ? <ChevronUpIcon className="w-4 h-4 ml-1" /> : <ChevronDownIcon className="w-4 h-4 ml-1" />;
    };
  
   if (loading) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
      <div className="text-center">
        <div className="relative inline-block">
          {/* Spinner */}
          <div className="w-16 h-16 border-4 border-indigo-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-16 h-16 border-4 border-indigo-700 rounded-full animate-spin border-t-transparent"></div>
          
          {/* Inner icon */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <svg className="w-6 h-6 text-indigo-600 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
        </div>
        
        <h3 className="mt-6 text-xl font-semibold text-gray-800">Loading Your Data</h3>
        <p className="mt-2 text-gray-500">Please wait while we fetch your data...</p>
        
        {/* Loading steps */}
        <div className="mt-6 space-y-2">
          {['Fetching users', 'Loading statistics', 'Preparing all'].map((step, index) => (
            <div key={step} className="flex items-center justify-center space-x-2 text-sm text-gray-500">
              <div className={`w-1.5 h-1.5 rounded-full ${index === 0 ? 'bg-indigo-600 animate-pulse' : 'bg-gray-300'}`}></div>
              <span className={index === 0 ? 'text-indigo-600' : ''}>{step}</span>
              {index === 0 && (
                <svg className="w-3 h-3 text-indigo-600 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 p-4 md:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <div className="flex items-center gap-3">
                                <Users className="h-10 w-10 text-indigo-600" />
                                <div>
                                    <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                                        User Management
                                    </h1>
                                    <p className="text-gray-600 mt-2">View and manage all registered users</p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={() => window.location.href = '/admin/adduser'}
                            className="bg-indigo-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-indigo-700 transform hover:scale-105 transition-all duration-200 flex items-center gap-2 shadow-lg"
                        >
                            <User className="h-5 w-5" />
                            Add New User
                        </button>
                    </div>
                </div>

                {/* Statistics Cards - First Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-indigo-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Total Users</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.total}</p>
                            </div>
                            <div className="bg-indigo-100 rounded-full p-3">
                                <Users className="h-6 w-6 text-indigo-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-purple-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Administrators</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.admins}</p>
                                <p className="text-xs text-purple-600 mt-1">{((stats.admins / stats.total) * 100 || 0).toFixed(1)}% of total</p>
                            </div>
                            <div className="bg-purple-100 rounded-full p-3">
                                <Shield className="h-6 w-6 text-purple-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-blue-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Inspectors</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.inspectors}</p>
                                <p className="text-xs text-blue-600 mt-1">{((stats.inspectors / stats.total) * 100 || 0).toFixed(1)}% of total</p>
                            </div>
                            <div className="bg-blue-100 rounded-full p-3">
                                <Briefcase className="h-6 w-6 text-blue-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-6 hover:shadow-lg transition-all duration-300 border-l-4 border-green-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-sm font-medium">Active Users</p>
                                <p className="text-3xl font-bold text-gray-800 mt-1">{stats.activeUsers}</p>
                                <p className="text-xs text-green-600 mt-1">{((stats.activeUsers / stats.total) * 100 || 0).toFixed(1)}% of total</p>
                            </div>
                            <div className="bg-green-100 rounded-full p-3">
                                <UserCheck className="h-6 w-6 text-green-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Statistics Cards - Second Row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                    <div className="bg-white rounded-2xl shadow-md p-4 hover:shadow-lg transition-all duration-300 border-l-4 border-red-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-xs font-medium">Inactive Users</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.inactiveUsers}</p>
                            </div>
                            <div className="bg-red-100 rounded-full p-2">
                                <UserX className="h-5 w-5 text-red-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-4 hover:shadow-lg transition-all duration-300 border-l-4 border-teal-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-xs font-medium">Other Roles</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.otherUsers}</p>
                            </div>
                            <div className="bg-teal-100 rounded-full p-2">
                                <User className="h-5 w-5 text-teal-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-4 hover:shadow-lg transition-all duration-300 border-l-4 border-orange-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-xs font-medium">New This Month</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.newThisMonth}</p>
                            </div>
                            <div className="bg-orange-100 rounded-full p-2">
                                <Calendar className="h-5 w-5 text-orange-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-4 hover:shadow-lg transition-all duration-300 border-l-4 border-cyan-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-xs font-medium">Today</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.todayUsers}</p>
                            </div>
                            <div className="bg-cyan-100 rounded-full p-2">
                                <TrendingUp className="h-5 w-5 text-cyan-600" />
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-2xl shadow-md p-4 hover:shadow-lg transition-all duration-300 border-l-4 border-pink-500">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-gray-500 text-xs font-medium">Recent (7d)</p>
                                <p className="text-2xl font-bold text-gray-800">{stats.recentUsers}</p>
                            </div>
                            <div className="bg-pink-100 rounded-full p-2">
                                <Award className="h-5 w-5 text-pink-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Filters and Search */}
                <div className="bg-indigo-700 rounded-lg p-4 mb-6 shadow-lg">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search by name, email, phone..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="w-full pl-10 pr-4 py-2 border-2 border-indigo-300 rounded-lg focus:outline-none focus:border-indigo-500 bg-white"
                            />
                        </div>

                        {/* Role Filter */}
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 w-5 h-5" />
                            <select
                                value={roleFilter}
                                onChange={handleRoleChange}
                                className="w-full pl-10 pr-4 py-2 border-2 border-indigo-300 rounded-lg focus:outline-none focus:border-indigo-500 appearance-none bg-white"
                            >
                                <option value="">All Roles</option>
                                <option value="admin">Administrator</option>
                                <option value="inspector">Inspector</option>
                                <option value="user">User</option>
                            </select>
                        </div>

                        {/* Status Filter */}
                        <div className="relative">
                            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 w-5 h-5" />
                            <select
                                value={statusFilter}
                                onChange={handleStatusChange}
                                className="w-full pl-10 pr-4 py-2 border-2 border-indigo-300 rounded-lg focus:outline-none focus:border-indigo-500 appearance-none bg-white"
                            >
                                <option value="">All Status</option>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>

                        {/* Per Page */}
                        <div className="relative">
                            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-600 font-medium">Show</span>
                            <select
                                value={perPage}
                                onChange={handlePerPageChange}
                                className="w-full pl-16 pr-4 py-2 border-2 border-indigo-300 rounded-lg focus:outline-none focus:border-indigo-500 appearance-none bg-white"
                            >
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-indigo-700">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600 transition-colors" onClick={() => handleSort('name')}>
                                        <div className="flex items-center gap-1">
                                            <User className="w-4 h-4" />
                                            Name
                                            {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600 transition-colors" onClick={() => handleSort('email')}>
                                        <div className="flex items-center gap-1">
                                            <Mail className="w-4 h-4" />
                                            Email
                                            {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden md:table-cell cursor-pointer" onClick={() => handleSort('phone')}>
                                        <div className="flex items-center gap-1">
                                            <Phone className="w-4 h-4" />
                                            Phone
                                            {sortField === 'phone' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider">
                                        <div className="flex items-center gap-1">
                                            <Shield className="w-4 h-4" />
                                            Role
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider hidden lg:table-cell">
                                        <div className="flex items-center gap-1">
                                            <Building className="w-4 h-4" />
                                            Address
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')}>
                                        <div className="flex items-center gap-1">
                                            Status
                                            {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-right text-xs font-medium text-white uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {error ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-8 text-center">
                                            <AlertCircle className="w-12 h-12 mx-auto mb-2 text-red-500" />
                                            <p className="text-red-600">{error}</p>
                                            <button
                                                onClick={() => fetchUsers()}
                                                className="mt-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                                            >
                                                Try Again
                                            </button>
                                        </td>
                                    </tr>
                                ) : paginatedUsers.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-8 text-center">
                                            <Users className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                                            <p className="text-gray-500">No users found.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedUsers.map((user) => (
                                        <tr key={user.id} className="hover:bg-indigo-50 transition duration-150">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {user.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {user.email}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 hidden md:table-cell">
                                                {user.phone || '—'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <RoleBadge role={user.role || 'user'} />
                                            </td>
                                            <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate hidden lg:table-cell">
                                                {user.address || '—'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge status={user.status || 'active'} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex justify-end space-x-2">
                                                    <button
                                                        onClick={() => handleView(user)}
                                                        className="p-1 text-indigo-600 hover:text-indigo-800 transition rounded-lg hover:bg-indigo-100"
                                                        title="View Details"
                                                    >
                                                        <Eye className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEdit(user.id)}
                                                        className="p-1 text-blue-600 hover:text-blue-800 transition rounded-lg hover:bg-blue-100"
                                                        title="Edit"
                                                    >
                                                        <Edit className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleToggleStatus(user.id, user.status)}
                                                        className={`p-1 transition rounded-lg ${
                                                            (user.status || 'active') === 'active' 
                                                                ? 'text-red-600 hover:text-red-800 hover:bg-red-100' 
                                                                : 'text-green-600 hover:text-green-800 hover:bg-green-100'
                                                        }`}
                                                        title={(user.status || 'active') === 'active' ? 'Deactivate' : 'Activate'}
                                                    >
                                                        {(user.status || 'active') === 'active' ? <XCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(user.id, user.name)}
                                                        className="p-1 text-red-600 hover:text-red-800 transition rounded-lg hover:bg-red-100"
                                                        title="Delete"
                                                    >
                                                        <Trash2 className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {!error && meta.last_page > 1 && (
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="text-sm text-gray-700">
                                Showing <span className="font-medium">{((meta.current_page - 1) * perPage) + 1}</span> to{' '}
                                <span className="font-medium">{Math.min(meta.current_page * perPage, meta.total)}</span> of{' '}
                                <span className="font-medium">{meta.total}</span> users
                            </div>
                            <div className="flex space-x-2">
                                <button
                                    onClick={() => handlePageChange(meta.current_page - 1)}
                                    disabled={meta.current_page === 1}
                                    className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </button>
                                <span className="px-4 py-1 bg-indigo-600 text-white rounded-lg">
                                    Page {meta.current_page} of {meta.last_page}
                                </span>
                                <button
                                    onClick={() => handlePageChange(meta.current_page + 1)}
                                    disabled={meta.current_page === meta.last_page}
                                    className="px-3 py-1 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default ViewUsers;